/**
 * Points, streaks, stars and WPM.
 *
 * Pure logic: no Phaser imports.
 *
 * Timed mode is deliberately absent. The star rules below award on accuracy;
 * when timed mode lands, par time becomes a second path to three stars and the
 * better of the two paths should win (see starsForAccuracy).
 */

/** Every correct word is worth this much before multipliers (CLAUDE.md). */
export const POINTS_PER_WORD = 10;

/** Bonus words add this much per character, on top of the flat word points. */
export const BONUS_POINTS_PER_CHAR = 5;

/** Paid once for reaching the goal. */
export const LEVEL_COMPLETE_BONUS = 50;

/** Wrong words cost nothing: the score never goes down (CLAUDE.md). */
export const MISS_PENALTY = 0;

export const STREAK_FOR_1_5X = 5;
export const STREAK_FOR_2X = 10;

/** Accuracy needed for each star while timed mode is deferred. */
export const THREE_STAR_ACCURACY = 95;
export const TWO_STAR_ACCURACY = 85;

/**
 * Multiplier for a word that brings the streak to `streak`.
 * The fifth word in a row is itself worth x1.5, the tenth x2.
 */
export function multiplierForStreak(streak: number): number {
  if (streak >= STREAK_FOR_2X) return 2;
  if (streak >= STREAK_FOR_1_5X) return 1.5;
  return 1;
}

/** Base value of a word before the streak multiplier. */
export function basePointsFor(word: string, isBonus: boolean): number {
  const bonus = isBonus ? BONUS_POINTS_PER_CHAR * word.length : 0;
  return POINTS_PER_WORD + bonus;
}

export interface ScoreState {
  readonly points: number;
  readonly streak: number;
  readonly longestStreak: number;
  readonly wordsCorrect: number;
  readonly wordsAttempted: number;
  readonly bonusWordsHit: number;
  /** Characters in correctly typed words, for net WPM. */
  readonly correctChars: number;
}

export function createScoreState(): ScoreState {
  return {
    points: 0,
    streak: 0,
    longestStreak: 0,
    wordsCorrect: 0,
    wordsAttempted: 0,
    bonusWordsHit: 0,
    correctChars: 0,
  };
}

/** A correct word: pay out, extend the streak. */
export function applyCorrect(state: ScoreState, word: string, isBonus: boolean): ScoreState {
  const streak = state.streak + 1;
  const earned = Math.round(basePointsFor(word, isBonus) * multiplierForStreak(streak));
  return {
    points: state.points + earned,
    streak,
    longestStreak: Math.max(state.longestStreak, streak),
    wordsCorrect: state.wordsCorrect + 1,
    wordsAttempted: state.wordsAttempted + 1,
    bonusWordsHit: state.bonusWordsHit + (isBonus ? 1 : 0),
    correctChars: state.correctChars + word.length,
  };
}

/** A wrong word: no points lost, but the streak resets (CLAUDE.md). */
export function applyMiss(state: ScoreState): ScoreState {
  return {
    ...state,
    points: state.points + MISS_PENALTY,
    streak: 0,
    wordsAttempted: state.wordsAttempted + 1,
  };
}

/** What a word would earn right now, for a floating "+35" later if wanted. */
export function previewPoints(state: ScoreState, word: string, isBonus: boolean): number {
  return Math.round(basePointsFor(word, isBonus) * multiplierForStreak(state.streak + 1));
}

export function finalScore(state: ScoreState, reachedGoal: boolean): number {
  return state.points + (reachedGoal ? LEVEL_COMPLETE_BONUS : 0);
}

/**
 * Stars from keystroke accuracy. Only a completed level earns stars, so a quit
 * or restarted level scores zero of them.
 *
 * When timed mode ships, compute its star count too and take the maximum, so
 * careful typing and fast typing are both routes to three stars.
 */
export function starsForAccuracy(accuracyPercent: number, reachedGoal: boolean): number {
  if (!reachedGoal) return 0;
  if (accuracyPercent >= THREE_STAR_ACCURACY) return 3;
  if (accuracyPercent >= TWO_STAR_ACCURACY) return 2;
  return 1;
}

/** Net words per minute: correct characters over five, per minute elapsed. */
export function wpmFor(correctChars: number, elapsedMs: number): number {
  if (elapsedMs <= 0 || correctChars <= 0) return 0;
  const minutes = elapsedMs / 60000;
  return correctChars / 5 / minutes;
}

/** CLAUDE.md: never punish. Below 70% accuracy we nudge, we do not scold. */
export const ACCURACY_TIP_THRESHOLD = 70;

export function encouragementFor(accuracyPercent: number, reachedGoal: boolean): string | null {
  if (accuracyPercent < ACCURACY_TIP_THRESHOLD) {
    return 'Try slowing down - accuracy first, speed comes next!';
  }
  if (!reachedGoal) return 'Every climb counts. Give it another go!';
  // 'Not a letter out of place' only when it is literally true: Results now
  // offers a list of what was missed, and the two must not disagree.
  if (accuracyPercent >= 100) return 'Brilliant typing. Not a letter out of place!';
  if (accuracyPercent >= THREE_STAR_ACCURACY) return 'Brilliant typing!';
  return null;
}
